import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ServerService } from './ServerService';

@Injectable({
  providedIn: 'root'
})
export class SuporteTicketsService extends ServerService {
  private endpoint = '/suporte-tickets';
  private endpointImplantacao = '/suporte/implantacao';

  constructor(protected http: HttpClient) {
    super(http);
  }

  liste(filtro: any = {}): Promise<any> {
    return this.obtenha(this.endpoint, filtro);
  }

  listeAbertosEmpresa(empresaId: number): Promise<any[]> {
    return this.obtenha(`${this.endpoint}/empresa/${empresaId}/abertos`, {});
  }

  listeAbertosContato(contatoId: number): Promise<any[]> {
    return this.obtenha(`${this.endpoint}/contato/${contatoId}/abertos`, {});
  }

  obtenhaTicket(ticketId: number): Promise<any> {
    return this.obtenha(`${this.endpoint}/${ticketId}`, {});
  }

  crie(ticket: any): Promise<any> {
    return this.facaPost(this.endpoint, ticket);
  }

  crieClientePkt(dados: any): Promise<any> {
    return this.facaPost(`${this.endpoint}/cliente-pkt`, dados);
  }

  reserveNotificacaoWhatsapp(): Promise<any> {
    return this.facaPost(`${this.endpoint}/notificacoes-whatsapp/reservar`, {});
  }

  concluaNotificacaoWhatsapp(id: number, dados: any): Promise<any> {
    return this.facaPost(`${this.endpoint}/notificacoes-whatsapp/${id}/concluir`, dados);
  }

  listeEmpresasImplantacao(): Promise<any[]> {
    return this.obtenha(`${this.endpointImplantacao}/empresas`, {});
  }

  crieGrupoImplantacao(empresaId: number): Promise<any> {
    return this.facaPost(`${this.endpointImplantacao}/empresas/${empresaId}/criar-grupo`, {});
  }

  concluaEmpresaImplantacao(empresaId: number): Promise<any> {
    return this.facaPost(`${this.endpointImplantacao}/empresas/${empresaId}/concluir`, {});
  }

  reserveImplantacaoWhatsapp(): Promise<any> {
    return this.facaPost(`${this.endpointImplantacao}/reservar`, {});
  }

  concluaImplantacaoWhatsapp(id: number, dados: any): Promise<any> {
    return this.facaPost(`${this.endpointImplantacao}/${id}/concluir`, dados);
  }

  reviseImplantacaoWhatsapp(id: number, dados: {
    acao: string; status: string; ordem: number; versao: number; grupoChatId?: string
  }): Promise<any> {
    return this.facaPost(`${this.endpointImplantacao}/${id}/revisar`, dados);
  }

  reserveNotificacaoCobrancaWhatsapp(): Promise<any> {
    return this.facaPost(`${this.endpoint}/notificacoes-cobranca-whatsapp/reservar`, {});
  }

  listeRevisaoCobrancaWhatsapp(): Promise<any[]> {
    return this.obtenha(`${this.endpoint}/notificacoes-cobranca-whatsapp/revisao`, {});
  }

  listeEmpresasRisco(): Promise<any> {
    return this.obtenha(`${this.endpoint}/empresas-risco`, {});
  }

  inicieAcompanhamentoEmpresaRisco(faturaId: number): Promise<any> {
    return this.facaPost(`${this.endpoint}/empresas-risco/${faturaId}/acompanhamento`, {});
  }

  reviseCobrancaWhatsapp(id: number, dados: {acao: string; status: string; ordem: number; versao: number}): Promise<any> {
    return this.facaPost(`${this.endpoint}/notificacoes-cobranca-whatsapp/${id}/revisar`, dados);
  }

  concluaNotificacaoCobrancaWhatsapp(id: number, dados: {
    token: string; ordem: number; sucesso: boolean; erro: string | null; incerto?: boolean
  }): Promise<any> {
    return this.facaPost(`${this.endpoint}/notificacoes-cobranca-whatsapp/${id}/concluir`, dados);
  }

  obtenhaContextoClientePkt(chatId: string, nomeGrupo: string): Promise<any> {
    return this.obtenha(`${this.endpoint}/cliente-pkt/contexto`, { chatId, nomeGrupo });
  }

  atualize(ticket: any): Promise<any> {
    return this.facaPut(`${this.endpoint}/${ticket.id}`, ticket);
  }

  altereResponsavel(ticketId: number, responsavelUsuarioId: number): Promise<any> {
    return this.facaPost(`${this.endpoint}/${ticketId}/responsavel`, { responsavelUsuarioId });
  }

  assuma(ticketId: number): Promise<any> {
    return this.facaPost(`${this.endpoint}/${ticketId}/assumir`, {});
  }

  envieParaDesenvolvimento(ticketId: number, dados: any): Promise<any> {
    return this.facaPost(`${this.endpoint}/${ticketId}/desenvolvimento`, dados);
  }

  mudeStatus(ticketId: number, status: string, observacao?: string): Promise<any> {
    const dados: any = { status };

    if (observacao && status === 'RESOLVIDO') {
      dados.resolucao = observacao;
    }

    if (observacao && status === 'CANCELADO') {
      dados.justificativaCancelamento = observacao;
    }

    return this.facaPost(`${this.endpoint}/${ticketId}/status`, dados);
  }

  adicioneInteracao(ticketId: number, texto: string, tipo: string = 'COMENTARIO'): Promise<any> {
    return this.facaPost(`${this.endpoint}/${ticketId}/interacoes`, { texto, tipo });
  }

  adicioneAnexo(ticketId: number, arquivo: File): Promise<any> {
    const formData = new FormData();
    formData.append('files', arquivo, arquivo.name);

    return this.http.post(`${this.endpoint}/${ticketId}/anexos`, formData)
      .toPromise()
      .then(this.retorno)
      .catch(this.handleError);
  }

  listeResponsaveis(): Promise<any[]> {
    return this.obtenha(`${this.endpoint}/responsaveis`, {});
  }

  minhasPermissoes(): Promise<any> {
    return this.obtenha(`${this.endpoint}/minhas-permissoes`, {});
  }

  obtenhaMinhaAtividadeImplantacao(): Promise<any> {
    return this.obtenha(`${this.endpoint}/minha-atividade-implantacao`, {});
  }

  inicieMinhaAtividadeImplantacao(duracaoMinutos: number = 90): Promise<any> {
    return this.facaPost(`${this.endpoint}/minha-atividade-implantacao/iniciar`, { duracaoMinutos });
  }

  encerreMinhaAtividadeImplantacao(): Promise<any> {
    return this.facaPost(`${this.endpoint}/minha-atividade-implantacao/encerrar`, {});
  }

  listeEquipe(): Promise<any[]> {
    return this.obtenha(`${this.endpoint}/equipe`, {});
  }

  salveUsuarioEquipe(usuarioId: number, dados: any): Promise<any> {
    return this.facaPost(`${this.endpoint}/equipe/${usuarioId}`, dados);
  }

  painelGestor(): Promise<any> {
    return this.obtenha(`${this.endpoint}/painel-gestor`, {});
  }

  painelIndividual(): Promise<any> {
    return this.obtenha(`${this.endpoint}/painel-individual`, {});
  }

  obtenhaConfiguracao(): Promise<any> {
    return this.obtenha(`${this.endpoint}/configuracao`, {});
  }

  atualizeConfiguracao(configuracao: any): Promise<any> {
    return this.facaPost(`${this.endpoint}/configuracao`, configuracao);
  }

  marqueAvaliacaoEnviada(avaliacaoId: number): Promise<any> {
    return this.facaPost(`/suporte-avaliacoes/${avaliacaoId}/enviada`, {});
  }

  listeAvaliacoesSuporte(filtro: any = {}): Promise<any> {
    return this.obtenha('/suporte-avaliacoes', filtro);
  }
}
